// components/ResultadoTabs/FoodTab.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  Label,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3 key={index} className="text-[16px] font-bold text-[#323334] mt-2 mb-1">
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">•</span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg className="animate-spin h-5 w-5 text-[#59AD31]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

// ───────────────────── Chart Helpers ─────────────────────

// Label dinámico para el eje Y (las dos gráficas)
const yAxisLabel = "kcal / cap / day";
const yAxisDx = -yAxisLabel.length; // pequeño desplazamiento hacia la izquierda

// ───────── helper para ordenar series por valor máximo ─────────
function getOrderByMaxValue(data, keys) {
  if (!data || !Array.isArray(data) || data.length === 0) return keys;

  return [...keys].sort((a, b) => {
    const maxA = Math.max(...data.map((d) => (typeof d[a] === "number" ? d[a] : 0)));
    const maxB = Math.max(...data.map((d) => (typeof d[b] === "number" ? d[b] : 0)));
    return maxB - maxA; // descendente
  });
}

function getTooltipIcon(entry, chartType = "bar") {
  const key = entry.dataKey;

  // Target metrics: always dashed
  if (key && (key.includes("Target") || key.includes("target"))) {
    return (
      <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
        <line x1="0" y1="1.5" x2="16" y2="1.5" stroke={entry.color} strokeWidth="2" strokeDasharray="3,2" />
      </svg>
    );
  }

  // Line charts: SVG solid line
  if (chartType === "line") {
    return (
      <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
        <line x1="0" y1="1" x2="16" y2="1" stroke={entry.color} strokeWidth="2" />
      </svg>
    );
  }

  // Standard box icon (bars, composed, defaults)
  return (
    <span
      className="h-3 w-3 rounded-[2px]"
      style={{ backgroundColor: entry.color }}
    />
  );
}

// ───────────────────── tooltip custom (ordenado por valor) ─────────────────────
function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>

      {sortedPayload.map((item) => (
        <div
          key={item.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            {getTooltipIcon(item, chartType)}
            <span className="text-[#7E8081]">{item.name}</span>
          </div>

          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? item.value.toLocaleString("en-US", { maximumFractionDigits: 2 })
              : item.value}{" "}
            <span className="text-[#98999A]">{yAxisLabel}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ───────── leyenda custom para ordenar las etiquetas ─────────
function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey)
  );

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const isTarget = entry.dataKey && (entry.dataKey.includes("Target") || entry.dataKey.includes("target"));

        let entryChartType = chartType;
        if (chartType === "bar") {
          if (entry.dataKey?.includes("Target") || entry.dataKey === "mder") {
            entryChartType = "line";
          }
        }

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            <span className="text-[12px]">{entry.value}</span>
            {entryChartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line
                  x1="0"
                  y1="1"
                  x2="20"
                  y2="1"
                  stroke={entry.color}
                  strokeWidth="2"
                  strokeDasharray={isTarget ? "3,2" : "0"}
                />
              </svg>
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function FoodTab({ data, pathway, pathwayId, country, onLoadingChange }) {
  // series ocultas en la gráfica 1
  const [hiddenIntakeKeys, setHiddenIntakeKeys] = useState([]);
  // series ocultas en la gráfica 2
  const [hiddenGroupKeys, setHiddenGroupKeys] = useState([]);
  const [intakeChartMode, setIntakeChartMode] = useState("bar");
  const [groupChartMode, setGroupChartMode] = useState("bar");

  const [intakeData, setIntakeData] = useState(data?.["Daily per capita food intake"] || []);
  const [groupData, setGroupData] = useState(data?.["Group food intake"] || []);

  // 🔄 Actualizar datos cuando cambian
  useEffect(() => {
    setIntakeData(data?.["Daily per capita food intake"] || []);
    setGroupData(data?.["Group food intake"] || []);
    setHiddenIntakeKeys([]);
    setHiddenGroupKeys([]);
  }, [data]);

  // States for LLM Analysis
  const [intakeAnalysis, setIntakeAnalysis] = useState("");
  const [groupAnalysis, setGroupAnalysis] = useState("");
  const [loadingIntakeAnalysis, setLoadingIntakeAnalysis] = useState(false);
  const [loadingGroupAnalysis, setLoadingGroupAnalysis] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loadingIntakeAnalysis || loadingGroupAnalysis);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingIntakeAnalysis, loadingGroupAnalysis]);

  const intakeLegendOrder = useMemo(() => {
    return getOrderByMaxValue(intakeData, [
      "feasibleKcal",
      "mder",
      "minTarget",
      "maxTarget",
    ]);
  }, [intakeData]);

  const groupLegendOrder = useMemo(() => {
    return getOrderByMaxValue(groupData, [
      "cereals",
      "fish",
      "fruitsVeg",
      "milk",
      "nuts",
      "poultry",
      "redMeat",
      "sugar",
      "others",
    ]);
  }, [groupData]);

  const fetchIntakeAnalysis = async () => {
    if (intakeData.length === 0 || !pathwayId || !country) {
      setIntakeAnalysis("");
      setLoadingIntakeAnalysis(false);
      return;
    }

    setLoadingIntakeAnalysis(true);
    try {
      const payload = {
        analysisKey: "intake_data_analysis",
        scope: "country",
        country: country,
        intakeData: {
          data: JSON.stringify(intakeData),
          title: `Daily per capita food intake - ${country}`,
        },
        context: "food",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Daily per capita food intake",
          },
        },
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/fablegpt`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);

      const resData = await response.json();
      setIntakeAnalysis(extractAnalysisText(resData, ["intakeDataAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching intake analysis:", error);
      setIntakeAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingIntakeAnalysis(false);
    }
  };

  const fetchGroupAnalysis = async () => {
    if (groupData.length === 0 || !pathwayId || !country) {
      setGroupAnalysis("");
      setLoadingGroupAnalysis(false);
      return;
    }

    setLoadingGroupAnalysis(true);
    try {
      const payload = {
        analysisKey: "group_data_analysis",
        scope: "country",
        country: country,
        groupData: {
          data: JSON.stringify(groupData),
          title: `Group food intake - ${country}`,
        },
        context: "food",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Group food intake",
          },
        },
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/fablegpt`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);

      const resData = await response.json();
      setGroupAnalysis(extractAnalysisText(resData, ["groupDataAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching group analysis:", error);
      setGroupAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingGroupAnalysis(false);
    }
  };

  useEffect(() => {
    fetchIntakeAnalysis();
    // eslint-disable-next-line
  }, [intakeData, pathway, pathwayId, country]);

  useEffect(() => {
    fetchGroupAnalysis();
    // eslint-disable-next-line
  }, [groupData, pathway, pathwayId, country]);

  const handleLegendClickIntake = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenIntakeKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey]
    );
  };

  const handleLegendClickGroup = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenGroupKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey]
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      {/* BLOQUE 1 */}
      <section className="flex w-full flex-col gap-4">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Daily per capita food consumption
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "line", label: "Line" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = intakeChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setIntakeChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                // Download image as JPG using html2canvas (chart only)
                const chartSection = document.getElementById('food-intake-chart-section');
                if (!chartSection) return;
                const description = 'Daily per capita food consumption';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                // Download data as CSV using papaparse, better formatted
                const description = 'Daily per capita food consumption';
                const Papa = (await import('papaparse')).default;
                if (!intakeData || !Array.isArray(intakeData) || intakeData.length === 0) return;
                // Readable headers
                const headers = Object.keys(intakeData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...intakeData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="food-intake-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-4 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={intakeData} margin={{ top: 40, right: 30, left: 60, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={50}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value={yAxisLabel}
                  angle={-90}
                  position="left"
                  dx={yAxisDx}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip content={(props) => <CustomTooltip {...props} chartType={intakeChartMode} />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />

              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={intakeLegendOrder}
                    hiddenKeys={hiddenIntakeKeys}
                    onToggle={handleLegendClickIntake}
                    chartType={intakeChartMode}
                  />
                )}
              />

              {intakeChartMode === "bar" ? (
                <Bar
                  dataKey="feasibleKcal"
                  name="Feasible"
                  fill="#97E6BA"
                  opacity={0.8}
                  radius={[4, 4, 0, 0]}
                  hide={hiddenIntakeKeys.includes("feasibleKcal")}
                />
              ) : intakeChartMode === "line" ? (
                <Line
                  type="monotone"
                  dataKey="feasibleKcal"
                  name="Feasible"
                  stroke="#59AD31"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                  hide={hiddenIntakeKeys.includes("feasibleKcal")}
                />
              ) : (
                <Area
                  type="monotone"
                  dataKey="feasibleKcal"
                  name="Feasible"
                  stroke="#59AD31"
                  fill="#97E6BA"
                  fillOpacity={0.35}
                  strokeWidth={2}
                  dot={false}
                  hide={hiddenIntakeKeys.includes("feasibleKcal")}
                />
              )}

              <Line
                type="monotone"
                dataKey="minTarget"
                name="Min Target"
                stroke="#ff0000"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenIntakeKeys.includes("minTarget")}
              />
              <Line
                type="monotone"
                dataKey="maxTarget"
                name="Max Target"
                stroke="#FFD786"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenIntakeKeys.includes("maxTarget")}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="w-full min-h-[100px]">
          {loadingIntakeAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchIntakeAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_daily_per_capita_food_consumption"><NativeAnalysis content={intakeAnalysis} /></div>
            </div>
          )}
        </div>
      </section>

      {/* BLOQUE 2 */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Daily per capita food consumption by group
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = groupChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setGroupChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                // Download image as JPG using html2canvas (chart only)
                const chartSection = document.getElementById('food-group-chart-section');
                if (!chartSection) return;
                const description = 'Daily per capita food consumption by group';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                // Download data as CSV using papaparse, better formatted
                const description = 'Daily per capita food consumption by group';
                const Papa = (await import('papaparse')).default;
                if (!groupData || !Array.isArray(groupData) || groupData.length === 0) return;
                // Readable headers
                const headers = Object.keys(groupData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...groupData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="food-group-chart-section" className="h-[700px] rounded-xl border border-[#EDF0F2] px-4 py-4">
          <ResponsiveContainer width="100%" height="100%">
            {groupChartMode === "bar" ? (
              <BarChart
                data={groupData}
                stackOffset="none"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={50}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                  }
                >
                  <Label
                    value={yAxisLabel}
                    angle={-90}
                    position="left"
                    dx={yAxisDx}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip content={(props) => <CustomTooltip {...props} chartType="bar" />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={groupLegendOrder}
                      hiddenKeys={hiddenGroupKeys}
                      onToggle={handleLegendClickGroup}
                      chartType="bar"
                    />
                  )}
                />

                <Bar dataKey="cereals" stackId="a" fill="#FFD786" name="Cereals" hide={hiddenGroupKeys.includes("cereals")} />
                <Bar dataKey="fish" stackId="a" fill="#9B59B6" name="Fish" hide={hiddenGroupKeys.includes("fish")} />
                <Bar dataKey="fruitsVeg" stackId="a" fill="#EAC65B" name="Fruits&vegs" hide={hiddenGroupKeys.includes("fruitsVeg")} />
                <Bar dataKey="milk" stackId="a" fill="#FB752E" name="Milk" hide={hiddenGroupKeys.includes("milk")} />
                <Bar dataKey="nuts" stackId="a" fill="#FFDDDB" name="Nuts" hide={hiddenGroupKeys.includes("nuts")} />
                <Bar dataKey="poultry" stackId="a" fill="#FF5579" name="Poultry" hide={hiddenGroupKeys.includes("poultry")} />
                <Bar dataKey="redMeat" stackId="a" fill="#009B8A" name="Red meat" hide={hiddenGroupKeys.includes("redMeat")} />
                <Bar dataKey="sugar" stackId="a" fill="#80D5FF" name="Sugar" hide={hiddenGroupKeys.includes("sugar")} />
                <Bar dataKey="others" stackId="a" fill="#E3E5E6" name="Others" hide={hiddenGroupKeys.includes("others")} />
              </BarChart>
            ) : (
              <ComposedChart
                data={groupData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={50}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                  }
                >
                  <Label
                    value={yAxisLabel}
                    angle={-90}
                    position="left"
                    dx={yAxisDx}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip content={(props) => <CustomTooltip {...props} chartType="area" />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={groupLegendOrder}
                      hiddenKeys={hiddenGroupKeys}
                      onToggle={handleLegendClickGroup}
                      chartType="area"
                    />
                  )}
                />

                <Area dataKey="cereals" stackId="a" type="monotone" stroke="#FFD786" fill="#FFD786" fillOpacity={0.35} name="Cereals" hide={hiddenGroupKeys.includes("cereals")} />
                <Area dataKey="fish" stackId="a" type="monotone" stroke="#9B59B6" fill="#9B59B6" fillOpacity={0.35} name="Fish" hide={hiddenGroupKeys.includes("fish")} />
                <Area dataKey="fruitsVeg" stackId="a" type="monotone" stroke="#EAC65B" fill="#EAC65B" fillOpacity={0.35} name="Fruits&vegs" hide={hiddenGroupKeys.includes("fruitsVeg")} />
                <Area dataKey="milk" stackId="a" type="monotone" stroke="#FB752E" fill="#FB752E" fillOpacity={0.35} name="Milk" hide={hiddenGroupKeys.includes("milk")} />
                <Area dataKey="nuts" stackId="a" type="monotone" stroke="#FFDDDB" fill="#FFDDDB" fillOpacity={0.35} name="Nuts" hide={hiddenGroupKeys.includes("nuts")} />
                <Area dataKey="poultry" stackId="a" type="monotone" stroke="#FF5579" fill="#FF5579" fillOpacity={0.35} name="Poultry" hide={hiddenGroupKeys.includes("poultry")} />
                <Area dataKey="redMeat" stackId="a" type="monotone" stroke="#009B8A" fill="#009B8A" fillOpacity={0.35} name="Red meat" hide={hiddenGroupKeys.includes("redMeat")} />
                <Area dataKey="sugar" stackId="a" type="monotone" stroke="#80D5FF" fill="#80D5FF" fillOpacity={0.35} name="Sugar" hide={hiddenGroupKeys.includes("sugar")} />
                <Area dataKey="others" stackId="a" type="monotone" stroke="#E3E5E6" fill="#E3E5E6" fillOpacity={0.35} name="Others" hide={hiddenGroupKeys.includes("others")} />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>

        <div className="w-full min-h-[100px]">
          {loadingGroupAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchGroupAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_daily_per_capita_food_consumption_by_group"><NativeAnalysis content={groupAnalysis} /></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}